import React, { useState, useRef, useEffect, useCallback } from 'react';
import SearchBar from './components/SearchBar';
import GraphVisualization from './components/GraphVisualization';
import PathResults from './components/PathResults';
import ExportControls from './components/ExportControls';
import EntityPanel from './components/EntityPanel';
import FeedbackModal from './components/FeedbackModal';
import axios from 'axios';
import config from './config';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import './App.css';

// Toast notification system
function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 300);
    }, 3500);
  }, []);

  return { toasts, addToast };
}

// Confetti burst
function triggerConfetti() {
  const container = document.createElement('div');
  container.className = 'confetti-container';
  document.body.appendChild(container);

  const colors = ['#667eea', '#764ba2', '#f39c12', '#e74c3c', '#2ecc71', '#3498db', '#e91e63', '#00bcd4'];
  for (let i = 0; i < 60; i++) {
    const piece = document.createElement('div');
    piece.className = 'confetti-piece';
    piece.style.left = Math.random() * 100 + '%';
    piece.style.background = colors[Math.floor(Math.random() * colors.length)];
    piece.style.animationDelay = Math.random() * 0.8 + 's';
    piece.style.animationDuration = (2 + Math.random() * 2) + 's';
    piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
    piece.style.width = (6 + Math.random() * 8) + 'px';
    piece.style.height = (6 + Math.random() * 8) + 'px';
    container.appendChild(piece);
  }

  setTimeout(() => container.remove(), 4000);
}

function App() {
  const { t } = useTranslation();
  const [pathData, setPathData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [stats, setStats] = useState({ entities: 0, relationships: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [panelEntity, setPanelEntity] = useState(null);
  const [prefillEntity, setPrefillEntity] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [resetSpinning, setResetSpinning] = useState(false);
  const graphRef = useRef(null);
  const { toasts, addToast } = useToast();

  const handleReset = () => {
    setPathData(null);
    setGraphData(null);
    setPanelEntity(null);
    setPrefillEntity(null);
    setResetKey(k => k + 1);
    setResetSpinning(true);
    setTimeout(() => setResetSpinning(false), 600);
  };

  // Fetch graph stats — poll every 5s until graph is loaded
  useEffect(() => {
    let interval;
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/stats`);
        const data = response.data;
        setStats({
          entities: data.entities || 0,
          relationships: data.relationships || 0
        });
        if (data.entities > 0 && !data.loading) {
          setStatsLoading(false);
          clearInterval(interval);
        }
      } catch {
        // keep polling
      }
    };
    fetchStats();
    interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const handlePathFound = (data) => {
    setPathData(data);

    // Toast + confetti logic
    if (data.found && data.paths && data.paths.length > 0) {
      const pathLength = data.paths[0].length;
      if (pathLength >= 5) {
        triggerConfetti();
        addToast(t('app.toast_complex_path', { length: pathLength, count: data.count }), 'success');
      } else {
        addToast(t('app.toast_path_found', { length: pathLength, count: data.count }), 'success');
      }
    } else if (data.exploration) {
      addToast(t('app.toast_exploring', { name: data.entity?.name || 'entity', count: data.neighbor_count }), 'info');
    }

    // Close entity panel on every new search
    setPanelEntity(null);

    // If preserveGraph is true, merge with existing graph instead of replacing
    if (data.preserveGraph && graphRef.current) {
      graphRef.current.addSubgraph(
        data.subgraph.nodes,
        data.subgraph.edges,
        data.pathHighlight?.path
      );
    } else if (!data.preserveGraph) {
      setGraphData(data.subgraph);
    }
  };

  const handleNodeClick = useCallback((nodeData, neighborData) => {
    setPanelEntity({ entity: nodeData, neighbors: neighborData });
  }, []);

  const handlePanelClose = () => {
    setPanelEntity(null);
  };

  const handleFindPathsFrom = (entity) => {
    setPanelEntity(null);
    setPrefillEntity({ id: entity.id, name: entity.label || entity.id, type: entity.type });
  };

  // Extract subgraph nodes for risk scoring
  const subgraphNodes = pathData?.subgraph?.nodes || graphData?.nodes || [];

  return (
    <div className="App">
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type} ${toast.exiting ? 'exiting' : ''}`}>
            <span>{toast.type === 'success' ? '\u2713' : toast.type === 'error' ? '\u2717' : '\u2139'}</span>
            {toast.message}
          </div>
        ))}
      </div>

      {/* Hero Section */}
      <header className="hero">
        <button
          className={`hero-reset-btn ${resetSpinning ? 'spinning' : ''}`}
          onClick={handleReset}
          title={t('app.reset_tooltip')}
        >
          ↺
        </button>
        <button
          className="hero-lang-btn"
          onClick={() => {
            const next = i18n.language === 'en' ? 'cs' : 'en';
            i18n.changeLanguage(next);
            localStorage.setItem('prepify_lang', next);
          }}
          title={i18n.language === 'en' ? 'Přepnout do češtiny' : 'Switch to English'}
        >
          {i18n.language === 'en' ? 'CS' : 'EN'}
        </button>
        <div className="hero-content">
          <div className="hero-logo">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="14" cy="14" r="6" fill="#fff" opacity="0.9"/>
              <circle cx="34" cy="14" r="4" fill="#fff" opacity="0.7"/>
              <circle cx="24" cy="36" r="5" fill="#fff" opacity="0.8"/>
              <line x1="18" y1="17" x2="30" y2="14" stroke="#fff" strokeWidth="2" opacity="0.5"/>
              <line x1="16" y1="19" x2="22" y2="32" stroke="#fff" strokeWidth="2" opacity="0.5"/>
              <line x1="32" y1="18" x2="26" y2="32" stroke="#fff" strokeWidth="2" opacity="0.5"/>
            </svg>
          </div>
          <h1 className="hero-title">Prepify Graph</h1>
          <p className="hero-tagline">{t('app.tagline')}</p>
          <div className="hero-stats">
            {statsLoading ? (
              <div className="stat-skeleton">
                <div className="skeleton-bar" /><div className="skeleton-bar" />
              </div>
            ) : (
              <>
                <div className="stat">
                  <span className="stat-number">{stats.entities.toLocaleString()}</span>
                  <span className="stat-label">{t('app.entities')}</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                  <span className="stat-number">{stats.relationships.toLocaleString()}</span>
                  <span className="stat-label">{t('app.relationships')}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <SearchBar key={resetKey} onPathFound={handlePathFound} graphRef={graphRef} prefillEntity={prefillEntity} />

      <div className={`main-content ${panelEntity ? 'panel-open' : ''}`}>
        <div className="graph-section">
          <GraphVisualization
            ref={graphRef}
            graphData={graphData}
            pathHighlight={pathData?.pathHighlight || pathData?.paths?.[0]}
            onNodeClick={handleNodeClick}
          />

          {pathData && (
            <ExportControls
              graphRef={graphRef}
              pathData={pathData}
            />
          )}
        </div>

        {panelEntity ? (
          <EntityPanel
            entity={panelEntity.entity}
            neighbors={panelEntity.neighbors}
            onClose={handlePanelClose}
            onFindPaths={handleFindPathsFrom}
          />
        ) : (
          <div className="results-section">
            <PathResults pathData={pathData} subgraphNodes={subgraphNodes} />
          </div>
        )}
      </div>

      <footer className="footer">
        <p>{t('app.footer')} | <a href={i18n.language === 'cs' ? '/help-cs.html' : '/help.html'} className="footer-link">{t('app.user_guide')}</a> | &copy; 2026</p>
      </footer>

      <FeedbackModal />
    </div>
  );
}

export default App;
